import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LuMapPin, LuPencil, LuSearch, LuTrash2, LuUser, LuX } from 'react-icons/lu';
import { apiJson } from '../apiClient';
import { useAuth } from '../AuthContext';
import { SUCCESS_THEN_REDIRECT_MS } from '../utils/submitFeedback';
import { normalizeRole } from '../utils/userLocale';
import {
  TANZANIA_REGIONS,
  getDistrictsByRegion,
  getDistrictNameResolved,
  getRegionName,
} from '../utils/tanzaniaLocations';
import './FactorySuppliersPage.css';
import './AutomotiveSuppliersPage.css';

const emptyForm = () => ({
  name: '',
  phone: '',
  region: '',
  district: '',
  street: '',
  notes: '',
});

const supplierToForm = (s) => ({
  name: s?.name || '',
  phone: s?.phone || '',
  region: s?.region || '',
  district: s?.district || '',
  street: s?.street || '',
  notes: s?.notes || '',
});

function formatSupplierLocation(s) {
  const parts = [
    getRegionName(s?.region),
    getDistrictNameResolved(s?.region, s?.district),
    s?.street?.trim(),
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

export default function AutomotiveSuppliersPage({ onNavigate }) {
  const { user } = useAuth();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const [mode, setMode] = useState('list');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');

  const districtOptions = useMemo(
    () => (form.region ? getDistrictsByRegion(form.region) : []),
    [form.region]
  );

  const filterDistrictOptions = useMemo(
    () => (filterRegion ? getDistrictsByRegion(filterRegion) : []),
    [filterRegion]
  );

  const regionsInList = useMemo(() => {
    const ids = new Set(items.map((s) => s.region).filter(Boolean));
    return TANZANIA_REGIONS.filter((r) => ids.has(r.id));
  }, [items]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter((s) => {
      if (filterRegion && s.region !== filterRegion) return false;
      if (filterDistrict && s.district !== filterDistrict) return false;
      if (!q) return true;
      const location = formatSupplierLocation(s).toLowerCase();
      const haystack = [
        s.name,
        s.phone,
        s.notes,
        location,
        getRegionName(s.region),
        getDistrictNameResolved(s.region, s.district),
        s.street,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, searchQuery, filterRegion, filterDistrict]);

  const hasActiveFilters = Boolean(searchQuery.trim() || filterRegion || filterDistrict);

  const clearFilters = () => {
    setSearchQuery('');
    setFilterRegion('');
    setFilterDistrict('');
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiJson('/suppliers');
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not load suppliers.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditId(null);
  };

  const openCreate = () => {
    resetForm();
    setError('');
    setSuccessMessage('');
    setMode('form');
  };

  const startEdit = (s) => {
    setEditId(String(s.id));
    setForm(supplierToForm(s));
    setError('');
    setSuccessMessage('');
    setMode('form');
  };

  const backToList = () => {
    resetForm();
    setError('');
    setMode('list');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isOwner) return;
    if (!form.name.trim()) {
      setError('Enter supplier name.');
      return;
    }
    if (!form.region) {
      setError('Choose region (mkoa).');
      return;
    }
    if (!form.district) {
      setError('Choose district (wilaya).');
      return;
    }

    setSaving(true);
    setError('');
    const payload = {
      name: form.name.trim(),
      phone: form.phone.trim() || undefined,
      region: form.region,
      district: form.district,
      street: form.street.trim() || undefined,
      notes: form.notes.trim() || undefined,
    };

    try {
      if (editId) {
        await apiJson(`/owner/suppliers/${editId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        await apiJson('/owner/suppliers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      resetForm();
      await load();
      setSuccessMessage(
        editId
          ? `✓ Supplier "${payload.name}" updated successfully.`
          : `✓ Supplier "${payload.name}" added successfully.`
      );
      setMode('list');
      window.setTimeout(() => setSuccessMessage(''), SUCCESS_THEN_REDIRECT_MS + 800);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not save supplier.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove supplier "${s.name}"?`)) return;
    setActionId(s.id);
    setError('');
    try {
      await apiJson(`/owner/suppliers/${s.id}`, { method: 'DELETE' });
      if (String(editId) === String(s.id)) resetForm();
      await load();
      setSuccessMessage(`✓ Supplier "${s.name}" removed.`);
      window.setTimeout(() => setSuccessMessage(''), SUCCESS_THEN_REDIRECT_MS + 800);
    } catch (err) {
      setError(err.body?.message || err.message || 'Could not remove supplier.');
    } finally {
      setActionId(null);
    }
  };

  if (mode === 'form') {
    return (
      <section className="factory-suppliers auto-suppliers page-bleed">
        <header className="auto-suppliers__head">
          <div>
            <h1 className="factory-suppliers__title">{editId ? 'Edit supplier' : 'Add new supplier'}</h1>
            <p className="factory-suppliers__sub">
              Save supplier contact and location. They appear when creating purchase orders.
            </p>
          </div>
          <button type="button" className="auto-suppliers__back" onClick={backToList} disabled={saving}>
            ← Back to suppliers
          </button>
        </header>

        {error ? (
          <p className="factory-suppliers__error" role="alert">
            {error}
          </p>
        ) : null}

        {!isOwner ? (
          <p className="factory-suppliers__muted">Only the owner can add or edit suppliers.</p>
        ) : (
          <form className="factory-suppliers__form" onSubmit={handleSubmit}>
            <section className="factory-suppliers__block">
              <div className="factory-suppliers__block-head">
                <span className="factory-suppliers__block-icon" aria-hidden>
                  <LuUser size={16} />
                </span>
                <h3 className="factory-suppliers__block-title">Supplier details</h3>
              </div>
              <div className="factory-suppliers__fields factory-suppliers__fields--2">
                <label className="factory-suppliers__field">
                  <span className="factory-suppliers__label">
                    Name <em>*</em>
                  </span>
                  <input
                    className="factory-suppliers__control"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Auto Parts Ltd, Nippon Motors"
                    disabled={saving}
                  />
                </label>
                <label className="factory-suppliers__field">
                  <span className="factory-suppliers__label">Phone</span>
                  <input
                    className="factory-suppliers__control"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="e.g. 0712 345 678"
                    disabled={saving}
                  />
                </label>
              </div>
            </section>

            <section className="factory-suppliers__block factory-suppliers__block--location">
              <div className="factory-suppliers__block-head">
                <span className="factory-suppliers__block-icon factory-suppliers__block-icon--location" aria-hidden>
                  <LuMapPin size={16} />
                </span>
                <div>
                  <h3 className="factory-suppliers__block-title">Physical location</h3>
                  <p className="factory-suppliers__block-hint">Choose mkoa and wilaya, then add mtaa if needed.</p>
                </div>
              </div>
              <div className="factory-suppliers__fields factory-suppliers__fields--2">
                <label className="factory-suppliers__field">
                  <span className="factory-suppliers__label">
                    Region (Mkoa) <em>*</em>
                  </span>
                  <select
                    className="factory-suppliers__control factory-suppliers__control--select"
                    value={form.region}
                    onChange={(e) => setForm((f) => ({ ...f, region: e.target.value, district: '' }))}
                    disabled={saving}
                  >
                    <option value="">Choose region…</option>
                    {TANZANIA_REGIONS.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="factory-suppliers__field">
                  <span className="factory-suppliers__label">
                    District (Wilaya) <em>*</em>
                  </span>
                  <select
                    className="factory-suppliers__control factory-suppliers__control--select"
                    value={form.district}
                    onChange={(e) => setForm((f) => ({ ...f, district: e.target.value }))}
                    disabled={saving || !form.region}
                  >
                    <option value="">{form.region ? 'Choose district…' : 'Select region first'}</option>
                    {districtOptions.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="factory-suppliers__field factory-suppliers__field--full">
                  <span className="factory-suppliers__label">Street / area (Mtaa)</span>
                  <input
                    className="factory-suppliers__control"
                    value={form.street}
                    onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
                    placeholder="e.g. Ubungo Industrial, Nyerere Road"
                    disabled={saving}
                  />
                </label>
              </div>
            </section>

            <section className="factory-suppliers__block factory-suppliers__block--notes">
              <label className="factory-suppliers__field factory-suppliers__field--full">
                <span className="factory-suppliers__label">Notes</span>
                <input
                  className="factory-suppliers__control"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional — delivery hours, contact person, etc."
                  disabled={saving}
                />
              </label>
            </section>

            <div className="factory-suppliers__form-footer">
              <button type="button" className="auto-suppliers__back auto-suppliers__back--inline" onClick={backToList} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="factory-suppliers__submit" disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Save changes' : 'Save supplier'}
              </button>
            </div>
          </form>
        )}
      </section>
    );
  }

  return (
    <section className="factory-suppliers auto-suppliers page-bleed">
      <header className="auto-suppliers__head">
        <div>
          <h1 className="factory-suppliers__title">Suppliers</h1>
          <p className="factory-suppliers__sub">
            Wasambazaji wako wa spare parts — jina, simu, na eneo. Wataonekana unapofanya purchase order.
          </p>
        </div>
        {isOwner ? (
          <button type="button" className="auto-suppliers__add" onClick={openCreate}>
            + Add new supplier
          </button>
        ) : null}
      </header>

      {successMessage ? (
        <div className="auto-suppliers__success" role="status">
          {successMessage}
        </div>
      ) : null}

      {error ? (
        <p className="factory-suppliers__error" role="alert">
          {error}
        </p>
      ) : null}

      <section className="factory-suppliers__list-section">
        {loading ? (
          <p className="factory-suppliers__muted">Loading…</p>
        ) : items.length === 0 ? (
          <div className="auto-suppliers__empty">
            <p className="factory-suppliers__muted">No suppliers yet.</p>
            {isOwner ? (
              <button type="button" className="auto-suppliers__add" onClick={openCreate}>
                + Add new supplier
              </button>
            ) : (
              <p className="factory-suppliers__muted">Only the owner can add suppliers.</p>
            )}
          </div>
        ) : (
          <>
            <div className="factory-suppliers__filters">
              <div className="factory-suppliers__search-wrap">
                <LuSearch className="factory-suppliers__search-icon" size={16} aria-hidden />
                <input
                  type="search"
                  className="factory-suppliers__search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search name, phone, location, notes…"
                  aria-label="Search suppliers"
                />
              </div>
              <select
                className="factory-suppliers__filter-select"
                value={filterRegion}
                onChange={(e) => {
                  setFilterRegion(e.target.value);
                  setFilterDistrict('');
                }}
                aria-label="Filter by region"
              >
                <option value="">All regions</option>
                {regionsInList.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <select
                className="factory-suppliers__filter-select"
                value={filterDistrict}
                onChange={(e) => setFilterDistrict(e.target.value)}
                disabled={!filterRegion}
                aria-label="Filter by district"
              >
                <option value="">{filterRegion ? 'All districts' : 'Select region first'}</option>
                {filterDistrictOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
              {hasActiveFilters ? (
                <button type="button" className="factory-suppliers__clear-filters" onClick={clearFilters}>
                  <LuX size={14} aria-hidden /> Clear
                </button>
              ) : null}
            </div>

            {!loading && items.length > 0 ? (
              <p className="factory-suppliers__list-meta">
                {filteredItems.length} of {items.length} supplier{items.length !== 1 ? 's' : ''}
                {hasActiveFilters ? ' matching filters' : ''}
              </p>
            ) : null}

            {filteredItems.length === 0 ? (
              <div className="factory-suppliers__empty-filter">
                <p>No suppliers match your search or filters.</p>
                <button type="button" className="factory-suppliers__clear-filters" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="factory-suppliers__table-wrap">
                <table className="factory-suppliers__table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Location</th>
                      <th>Notes</th>
                      {isOwner ? <th>Action</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((s) => {
                      const busy = String(actionId) === String(s.id);
                      const regionName = getRegionName(s.region);
                      const districtName = getDistrictNameResolved(s.region, s.district);
                      return (
                        <tr key={s.id}>
                          <td className="factory-suppliers__name-cell">{s.name}</td>
                          <td>{s.phone || '—'}</td>
                          <td className="factory-suppliers__location-cell">
                            {regionName || districtName || s.street ? (
                              <div className="factory-suppliers__location-tags">
                                {regionName ? <span className="factory-suppliers__tag">{regionName}</span> : null}
                                {districtName ? (
                                  <span className="factory-suppliers__tag factory-suppliers__tag--district">
                                    {districtName}
                                  </span>
                                ) : null}
                                {s.street?.trim() ? (
                                  <span className="factory-suppliers__tag factory-suppliers__tag--street">
                                    {s.street.trim()}
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="factory-suppliers__notes-cell">{s.notes || '—'}</td>
                          {isOwner ? (
                            <td>
                              <div className="factory-suppliers__actions">
                                <button
                                  type="button"
                                  className="factory-suppliers__icon"
                                  onClick={() => startEdit(s)}
                                  disabled={actionId != null}
                                  title="Edit supplier"
                                >
                                  <LuPencil size={15} />
                                </button>
                                <button
                                  type="button"
                                  className="factory-suppliers__icon factory-suppliers__icon--danger"
                                  onClick={() => void remove(s)}
                                  disabled={actionId != null}
                                  title="Remove supplier"
                                >
                                  <LuTrash2 size={15} />
                                </button>
                              </div>
                              {busy ? <span className="factory-suppliers__busy">Removing…</span> : null}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {typeof onNavigate === 'function' ? (
        <button type="button" className="factory-suppliers__link" onClick={() => onNavigate('orders-make')}>
          Go to new purchase order →
        </button>
      ) : null}
    </section>
  );
}
